import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { navigate } from 'gatsby';
import {
  Layout,
  routes,
  siteName,
  siteDescription,
  pageTypes,
  AdMobileAnchor,
  AdMobileBanner,
  AdLeaderboard
} from 'gatsby-theme-apollo';

import Footer from '../templates/page/footer';
import GameContent from '../templates/page/content';
import TierListPreviewCards from '../templates/cards/tier-list-preview-card';
import Profile from '../templates/social/profile';
import StatusMessage from '../templates/status/status-message';
import Section from '../templates/layout/section';
import { generateTierListPreviews } from '../util/strip-data';
import PostPreviewCards from '../templates/cards/post-preview-card';

const GET_USER = gql`
  query GetUser($userName: String!){
    authors(where: { name_contains: $userName}) {
      name
      bio
      twitterUsername
      youtubeUsername
      youtubeChannelId
      twitchUsername
      discordServerName
      patreonUsername
      instagramUsername
      discordURI
      websiteURI
      id
      posts (sort: "createdAt:DESC"){
        title
        id
        publishedDate
        permalink
        createdAt
        games {
          name
          id
        }
      }
      tierlists(
        sort: "lastPublish:DESC"
      ) {
        id
        title
        tierJSON
        description
        updatedAt
        permalink
        lastPublish
        createdAt
        author {
          name
          id
        }
        game {
          name
          id
          updatedAt
        }
      }
    }
  }
`

/**
  Renders user pages that aggregate tier lists associated with an author
  /user/<username>
*/
const UserPage = ({ location }) => {
  const pageType = pageTypes.USER;
  const path = location.pathname;
  const pathSegments = path.split('/');

  const userNameFromPath = pathSegments[pathSegments.length - 2];
  const userNameFromPathLowerCase = userNameFromPath && userNameFromPath.toLowerCase();
  const userName = userNameFromPathLowerCase && userNameFromPathLowerCase.replace(/_/g, ' ');

  const pageTitle = `Tier Lists and Guides by ${userName} | ${siteName}`;
  const pageDescription = `Check out ${userName}'s updated Tier Lists and Guides GamerHub. ${siteDescription}`;

  const UserQueryResult = () => {
    const { loading, error, data } = useQuery(GET_USER, {
      fetchPolicy: 'no-cache',
      variables: { userName: userName },
    });

    if (error) {
      console.log(error);
      return <p>Error :(</p>;
    }

    let authorProfile;

    if (!loading) {
      authorProfile = data.authors && data.authors.length && data.authors[0];
      if (!authorProfile) {
        navigate(routes.UnknownPage());
      }
    }

    if (loading) {
      return <StatusMessage>Loading User</StatusMessage>;
    }

    const tierListPreviews = generateTierListPreviews(
      authorProfile && authorProfile.tierlists
    );

    const { posts } = authorProfile;

    return (
      authorProfile && (
        <>
          <Profile profile={authorProfile} />
          <Section title="Guides" fullWidth>
            {posts && posts.length !== 0 ? (
              <PostPreviewCards postPreviews={posts} />
            ) : (
              <StatusMessage> None yet!</StatusMessage>
            )}
          </Section>
          <Section title={'Tier Lists'}>
            {tierListPreviews && tierListPreviews.length !== 0 ? (
              <TierListPreviewCards tierListPreviews={tierListPreviews} limit={24} />
            ) : (
              <StatusMessage> None yet!</StatusMessage>
            )}
          </Section>
        </>
      )
    );
  };


  return (
    <Layout
      location={location}
      title={pageTitle}
      description={pageDescription}
      pageType={pageType}
      pageProps={{ userName }}
    >
      <AdMobileAnchor placementId={'gh-anchor-' + pageType +'-bottom'} anchorType='bottom' offset='0' />
      <GameContent header={`${userName}'s Profile `}>
        <AdMobileBanner placementId={"gh-mobilebanner-" + pageType + "-top"} />
        <AdLeaderboard placementId={"gh-leaderboard-" + pageType + "-top"} />
        <UserQueryResult />
        <Footer />
      </GameContent>
    </Layout>
  );
};

export default UserPage;
